import { lazy, Suspense, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { NOVA_DESIGN_SHIPPED, IS_EARLY_ACCESS } from "@/config/launch"; // NOVA-KILL-1, LAUNCH-2
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { useNovaNudges } from "@/hooks/useNovaNudges";
import { useThemeMode } from "@/hooks/useThemeMode";
import { useGoogie } from "@/hooks/useGoogie";
import { useNovaIntelligence } from "@/hooks/useNovaIntelligence";
import { DemoModeProvider } from "@/contexts/DemoModeContext";
import { ShowcaseModeProvider } from "@/contexts/ShowcaseModeContext";
import { DemoStoreProvider } from "@/contexts/DemoStoreContext";
import { CinemaModeProvider } from "@/contexts/CinemaModeContext";
import { UpgradeModalProvider } from "@/contexts/UpgradeModalContext";
import { UpgradeModal } from "@/components/billing/UpgradeModal";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
// SCALE-2 — attaches user id + org id (uuids only) to error reports.
import { ObservabilityIdentity } from "@/components/shared/ObservabilityIdentity";
import { NovaEmotionProvider } from "@/components/nova/NovaEmotion";
// NCC-5 — primes the shared Creator Cast stylesheet + defs on first
// paint so every `<CreatorSceneEmbed />` downstream renders animated
// out of the box. Renders nothing.
import { CreatorCastAssets } from "@/components/nova/CreatorCastAssets";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import AdminRoute from "@/components/auth/AdminRoute";
// DEMO-2 — the DevOnlyRoute import is gone along with the two routes
// it gated (/aperture-demo, /playground). The component itself still
// lives at src/components/auth/DevOnlyRoute.tsx; re-import it here if
// those routes ever come back.
import OrbitLoader from "@/components/shared/OrbitLoader";
import SplashScreen, { useSplashScreen } from "@/components/shared/SplashScreen";
import ShowcaseTour from "@/components/shared/ShowcaseTour";
// Cookie consent banner — mounted globally (skips client-facing
// studio surfaces internally). Eager-imported because it's tiny and
// needs to evaluate on first paint to decide whether to show.
import CookieConsentBanner from "@/components/shared/CookieConsentBanner";
import { toast } from "@/hooks/use-toast";
// HOTFIX-4 — lets the global mutation-error toast carry a "Set up"
// action when a write fails because the account has no organization.
import { ToastAction } from "@/components/ui/toast";
import { startUpdateChecker } from "@/lib/updateChecker";
import { syncStorage } from "@/lib/storage";
// ONB-5 — decides where the wizard hands off when it doesn't say.
import { ONB5_FLOW, ONB5_DEV_SANDBOX } from "@/config/launch";
// ONB-5 — Passport is the LABEL; /galaxy is the route. Naming the
// constant after the label is how '/passport' got written here first,
// which would have been a silent 404 on the very last step of signup.
import { PASSPORT_PATH } from "@/config/routes";

// Eagerly loaded (critical path)
import Index from "./pages/Index";
import Auth from "./pages/Auth";
// LAUNCH-EA-1 — public waitlist capture. Eager-imported alongside
// Auth since marketing's "Sign up" CTA points straight here during
// the early-access window.
import EarlyAccessSignup from "./pages/EarlyAccessSignup";
import NotFound from "./pages/NotFound";
import RootLayout from "./components/shared/RootLayout";
import PlannerCalendar from "./components/planner/PlannerCalendar";
import { GoogieInvoices } from "./components/googie/screens/GoogieInvoices";
import { GoogieInvoiceEditor } from "./components/googie/screens/GoogieInvoiceEditor";

// P0-FIX-1 — Password-recovery landing page (companion to the
// "Forgot password?" dialog inside Auth.tsx). Lazy-loaded because
// the vast majority of sessions never touch it.
const AuthReset = lazy(() => import("./pages/AuthReset"));

// Lazy loaded pages (code splitting)
const ClientPortal = lazy(() => import("./pages/ClientPortal"));
const ProjectWorkspace = lazy(() => import("./pages/ProjectWorkspace"));
// GOOGIE-14 — Contract Studio is a screen component rather than a
// page, so it gets a thin route wrapper below that reads both ids
// off the URL. Lazy like every other heavy authoring surface.
const ContractStudioRoute = lazy(() => import("./pages/ContractStudioRoute"));
const IntakeForm = lazy(() => import("./pages/IntakeForm"));
const Inquiries = lazy(() => import("./pages/Inquiries"));
const BillingPage = lazy(() => import("./pages/BillingPage"));
import { PublicShell } from "@/components/public/PublicShell";
const PublicClientPortal = lazy(() => import("./pages/PublicClientPortal"));
// GAL-7 — QR landing for the "Mobile access" card. Shows the access
// code + browser fallback because Universal Links are dormant (no
// Associated Domains on the personal Apple team).
const PortalAppLanding = lazy(() => import("./pages/PortalAppLanding"));
const PublicIntakeForm = lazy(() => import("./pages/PublicIntakeForm"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const Settings = lazy(() => import("./pages/Settings"));
const SettingsTemplates = lazy(() => import("./pages/SettingsTemplates"));
const PixiesetMigration = lazy(() => import("./pages/PixiesetMigration"));
const PublicVideoProof = lazy(() => import("./pages/PublicVideoProof"));
const Projects = lazy(() => import("./pages/Projects"));
const Clients = lazy(() => import("./pages/Clients"));
const InquiryPortal = lazy(() => import("./pages/InquiryPortal"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Assistant = lazy(() => import("./pages/Assistant"));
// GOOGIE-1 — Nova under the Googie sky. The branch lives here rather
// than inside Assistant.tsx because GoogieNova MOUNTS Assistant (the
// redesign reframes Nova, it never rebuilds her), and a branch inside
// the page would be an import cycle.
const GoogieNova = lazy(() =>
  import("./components/googie/screens/GoogieNova").then((m) => ({ default: m.GoogieNova })),
);
const GoogieForms = lazy(() =>
  import("./components/googie/screens/GoogieForms").then((m) => ({ default: m.GoogieForms })),
);
const Deliveries = lazy(() => import("./pages/Deliveries"));
const Galleries = lazy(() => import("./pages/Galleries"));
// GAL-5 — full-screen gallery editor (/galleries/:galleryId).
// Pixieset-style build surface: media grid + drop zone, proofing,
// settings, share. The galleries hub + workspace GalleryTab both
// deep-link here.
const GalleryEditor = lazy(() => import("./pages/GalleryEditor"));
// #435 — /galaxy now routes to the redesigned Passport Progress
// surface; the legacy Galaxy page is retired.
const Galaxy = lazy(() => import("./pages/PassportProgress"));
const DeliveryDetail = lazy(() => import("./pages/DeliveryDetail"));
const PublicDelivery = lazy(() => import("./pages/PublicDelivery"));
// GAL-2 — standalone public gallery portal (`/gallery/:galleryId`)
// + demo sibling (`/gallery/demo`). The demo route is registered
// BEFORE the parameterized one so `demo` matches the demo handler
// instead of being treated as a gallery UUID.
const PublicGallery = lazy(() => import("./pages/PublicGallery"));
const DemoGalleryPortal = lazy(() =>
  import("./pages/PublicGallery").then((m) => ({ default: m.DemoGalleryPortal })),
);
// MKTG-GAL14c — the marketing page's "See it live" windows: one
// gallery design or one site template, full size, on the sample album.
const LiveDemoGallery = lazy(() =>
  import("./pages/LiveDemo").then((m) => ({ default: m.LiveDemoGallery })),
);
const LiveDemoSite = lazy(() =>
  import("./pages/LiveDemo").then((m) => ({ default: m.LiveDemoSite })),
);
const SignContract = lazy(() => import("./pages/SignContract"));
const SignDocument = lazy(() => import("./pages/SignDocument"));
const PublicBooking = lazy(() => import("./pages/PublicBooking"));
// FOUNDING-1 — a Founding Member's kit page (see the route below).
const FoundingKit = lazy(() => import("./pages/FoundingKit"));
// GIFT-1 — Rennan's ACT II · ORBIT thank-you gifts (private per-person link).
const Gift = lazy(() => import("./pages/Gift"));
// SHOP-1 — the Launch Edition shop (pre-orders), linked from the marketing nav.
const Shop = lazy(() => import("./pages/Shop"));
const NovaShowcase = lazy(() => import("./pages/NovaShowcase"));
// NOVA-ENV-1 — "Nova and the Environment": how much energy she uses.
// Public; the green button under Nova's cards on the marketing page.
const NovaEnvironment = lazy(() => import("./pages/NovaEnvironment"));
// #437 — 2026 Nova design kit demo (all 22 expressions + 8 personas).
const NovaKitShowcase = lazy(() => import("./pages/NovaKitShowcase"));
const OnboardingWizardPreview = lazy(() => import("./components/onboarding/OnboardingWizard"));
// ONB-4 — WelcomeSequence restored. Fresh signup order is:
//   /welcome → /onboarding (wizard) → /nova-intro → / (Today).
// The 10-slide editorial brand-story sequence sets the tone before
// the wizard collects business details, and NovaIntro lands after
// so studios have a business context when they meet Nova.
const WelcomeSequence = lazy(() => import("./pages/onboarding/WelcomeSequence"));
// ONB-5 — the setup-flow workbench.
//
// The ternary is load-bearing, not stylistic. `lazy(() => import(...))`
// at the top level makes Rollup emit the chunk whether or not any route
// renders it: a first pass shipped OnboardingSandbox-*.js to production,
// unreachable but downloadable by anyone who read the manifest. Vite
// replaces `import.meta.env.DEV` with the literal `false` in a
// production build, so the import() below sits in a dead branch and
// treeshaking removes the chunk outright. Verified by grepping dist/.
// DEVMODE-1 — the sandbox now ships, behind AdminRoute at
// /admin/preview/setup, as the dry-run setup preview. It is UI only
// (the wizard runs with `preview`, which writes nothing), so the
// chunk being in the bundle is no longer a leak.
const OnboardingSandbox = lazy(() => import("./pages/dev/OnboardingSandbox"));
const AdminPreview = lazy(() => import("./pages/admin/AdminPreview"));
// #436 — Post-wizard NovaIntro (mirror of iOS NovaIntroFlow).
const NovaIntro = lazy(() => import("./pages/onboarding/NovaIntro"));
const FeatureTour = lazy(() => import("./pages/onboarding/FeatureTour"));
// SIGNUP-4 — OrbitTourSequence is back, redesigned: six cards after
// "Welcome aboard", ending in the finale that opens Today. Mounted at
// /tour/orbit; the wizard lands there on completion.
const OrbitTourSequence = lazy(() => import("./components/onboarding/OrbitTourSequence"));
// Workflows import killed (#341) — no route surfaces it post Aug 29 cut.
// const Workflows = lazy(() => import("./pages/Workflows"));
const SitesAdmin = lazy(() => import("./pages/SitesAdmin"));
// Wave EC-2 — studio-facing Email Center. Route + sidebar entry wired
// alongside Sites since it's the same "studio output surface" family.
const EmailCenter = lazy(() => import("./pages/EmailCenter"));
// NCC-2 — Learn tab. Editorial surface where studios discover Orbit's
// features via a grid of Apple Home-style info cards. Sidebar entry
// lives near Nova; route stays a top-level page so /learn is a first-
// class URL that Nova can deep-link into.
const Learn = lazy(() => import("./pages/Learn"));
// SITES-V2 — the creator website templates (T1–T7) + the editor preview frame.
const PublicSiteV2 = lazy(() => import("./pages/PublicSiteV2"));
const SitePreviewFrame = lazy(() => import("./pages/SitePreviewFrame"));
const OAuthCallback = lazy(() => import("./pages/OAuthCallback"));
// Nova Design is now a mode inside Nova — Assistant.tsx embeds
// pages/NovaDesign.tsx directly, so no lazy route import here.
// /nova-design redirects to /assistant?mode=design (see below).
// DEMO-2 — the ApertureDemo (#494 rev) and DemoPlayground (#494 Pass
// 6) lazy imports were removed with their routes. Both pages render
// literal "Placeholder" cards, and DevOnlyRoute only 404s them for
// non-privileged visitors — a signed-in admin fumbling a URL on stage
// still lands on placeholder copy. Files kept at
// src/pages/ApertureDemo.tsx and src/pages/DemoPlayground.tsx.
import { NovaLauncher } from "@/components/nova/NovaLauncher";
// GAL-8 — background gallery uploads: the module-level manager gets
// the app's QueryClient at bootstrap (see attach call below).
import { attachUploadQueryClient } from "@/lib/uploads/galleryUploadManager";

// Delegates to the shared classifier so App routing and the
// custom-domain hooks agree on what counts as an Orbit host.
import { isClientFacingSurface, isCustomStudioHostname } from "@/lib/hostRouting";
const isCustomHostname = isCustomStudioHostname;
// Wave 6 Part 2 — wraps public routes so any verified custom
// domain's brand overrides (colors + font + logo) apply.
import { CustomDomainProvider } from "@/components/branding/CustomDomainProvider";
const Expenses = lazy(() => import("./pages/Expenses"));
const Marketing = lazy(() => import("./pages/Marketing"));

// DEMO-2 — the /m/* mobile shell imports were removed with their
// routes. See the routing block below for the full rationale; the
// short version is that the native iOS app is the mobile story now,
// and /m was an UNGATED public URL serving fixture data.
// Files kept: src/components/mobile/* and src/pages/mobile/*.

// Admin pages (lazy loaded)
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminAccounts = lazy(() => import("./pages/admin/AdminAccounts"));
const AdminAccountDetail = lazy(() => import("./pages/admin/AdminAccountDetail"));
const AdminEmailCenter = lazy(() => import("./pages/admin/AdminEmailCenter"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminAuditLog = lazy(() => import("./pages/admin/AdminAuditLog"));
const AdminRevenue = lazy(() => import("./pages/admin/AdminRevenue"));
const AdminBetaReadiness = lazy(() => import("./pages/admin/AdminBetaReadiness"));
const AdminBetaNda = lazy(() => import("./pages/admin/AdminBetaNda"));
const AdminBetaTesters = lazy(() => import("./pages/admin/AdminBetaTesters"));
const AdminDiagnostics = lazy(() => import("./pages/admin/AdminDiagnostics"));
const AdminNovaStudio = lazy(() => import("./pages/admin/AdminNovaStudio"));
// MC-1 — Nova in cofounder mode (admin-only growth-strategy chat).
const AdminNova = lazy(() => import("./pages/admin/AdminNova"));
const AdminDemoMedia = lazy(() => import("./pages/admin/AdminDemoMedia"));
// Wave 8 — admin grants Preview Studio access per user.
const AdminDemoAccess = lazy(() => import("./pages/admin/DemoAccess"));
// LAUNCH-EA-1 — waitlist review + invite ahead of the Sept 14 public
// launch. Same list doubles as the marketing send audience.
const AdminEarlyAccess = lazy(() => import("./pages/admin/AdminEarlyAccess"));
// FOUNDING-2 — Founding Member kits: picks, addresses, fulfilment.
const AdminFounding = lazy(() => import("./pages/admin/AdminFounding"));
const AdminShop = lazy(() => import("./pages/admin/AdminShop"));
// GIFT-1 — the thank-you gifts: who's been thanked, what they picked, where it ships.
const AdminGifts = lazy(() => import("./pages/admin/AdminGifts"));

// Loading fallback component.
// SITE-BRAND-1 — every client-facing page is lazy-loaded, so this was
// the first thing a studio's client saw on a published site, a proof
// gallery or a portal: Orbit's Nova loader. There it's a blank page
// instead, and the page paints its own brand when it arrives.
const PageLoader = () =>
  isClientFacingSurface() ? (
    <div className="min-h-screen" aria-busy="true" />
  ) : (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <OrbitLoader size="lg" />
    </div>
  );

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (cache retention)
    },
    mutations: {
      onError: (error) => {
        const description =
          error instanceof Error ? error.message : "Something tripped on the way through. Try again.";
        // HOTFIX-4 — service-layer writes throw "Organization not
        // found…" when a signed-in account has no org (see
        // services/inquiries.ts, services/intakeForms.ts). Belt and
        // braces on top of ProtectedRoute's forced setup: give the
        // toast a way OUT instead of dead-ending. /onboarding is
        // reachable without an org, so this can't loop.
        // UX-8 — services now throw in studio language ("Your studio
        // isn't set up yet"); the old phrasing stays matched so any
        // straggler (or cached bundle) keeps its way out too.
        const needsOrgSetup = /organization not found|studio isn.t set up/i.test(description);
        toast({
          title: "That didn't save",
          description,
          variant: "destructive",
          action: needsOrgSetup ? (
            <ToastAction
              altText="Complete your studio setup"
              onClick={() => window.location.assign("/onboarding")}
            >
              Set up
            </ToastAction>
          ) : undefined,
        });
      },
    },
  },
});

// GAL-8 — hand the app-wide client to the background upload
// manager. The manager outlives every page, so its completion
// invalidations (['studio-galleries'], ['gallery-photos', …]) must
// ride THIS client, not a page hook that may already be unmounted.
attachUploadQueryClient(queryClient);

// Inner app component that uses splash screen hook
const AppContent = () => {
  const { showSplash, handleComplete } = useSplashScreen();

  // Wave 13a — Real light/dark mode toggle restored.  `useThemeMode`
  // syncs `<html>`'s `data-theme`, `.dark`, and `.new-shell` classes
  // with the studio's chosen mode (persisted in localStorage under
  // `orbit_theme_mode`).  The initial paint is already correct
  // thanks to the pre-mount bootstrap in `main.tsx`; this hook keeps
  // the DOM in sync when the mode changes via the Appearance
  // settings toggle.
  //
  // The legacy `orbit.use_new_shell` flag stays pinned to "1" so any
  // `useNewShell()` consumer keeps behaving as before — the new
  // shell is the only shell now; light mode simply doesn't apply
  // the .new-shell class (see useThemeMode's applyThemeMode).
  useThemeMode();
  useEffect(() => {
    try {
      localStorage.setItem('orbit.use_new_shell', '1');
    } catch {
      /* private mode — no-op */
    }
  }, []);

  // Clear the stale-chunk reload-guard flag once the app successfully
  // mounts. ErrorBoundary sets this in sessionStorage when it auto-
  // reloads after a chunk-load failure; if we don't clear it on a
  // healthy boot, the NEXT deploy that ships during this session
  // would skip the auto-recovery (because the flag is still set).
  useEffect(() => {
    try {
      sessionStorage.removeItem('orbit_chunk_reload_attempted');
    } catch {
      // sessionStorage blocked (private mode / cookies disabled) —
      // nothing to do, the guard just won't run either.
    }
  }, []);

  // Non-intrusive deploy-detection toast. Polls /build.json on
  // interval + on tab focus; if a newer build than the one we
  // booted with is up, shows a "Refresh to apply" Sonner toast.
  // See src/lib/updateChecker.ts for the polling details.
  useEffect(() => {
    const stop = startUpdateChecker();
    return stop;
  }, []);

  // Nova's event-driven nudges — subscribes to nova_nudges realtime
  // and pops sonner toasts for new inquiries, overdue invoices, and
  // sub-48h deliverable/event deadlines. No-op when signed out.
  useNovaNudges();

  return (
    <>
      {showSplash && <SplashScreen onComplete={handleComplete} />}
      {/* NCC-5 — prime the Nova Creator Cast stylesheet + shared defs
          once at the app root so every `<CreatorSceneEmbed />` inside
          Learn / RolePicker / the orbit tour picks up animations
          without each surface having to inject them itself. Renders
          nothing. */}
      <CreatorCastAssets />
      <Toaster />
      <Sonner />
      <BrowserRouter>
        {/* Global cookie consent banner — decides internally whether
            to render (skips on client-facing studio surfaces like
            /portal, /s, /book, /embed so visitors aren't confused
            by an Orbit-branded banner on the studio's own domain). */}
        <CookieConsentBanner />
        <Suspense fallback={<PageLoader />}>
          {isCustomHostname() ? (
            // Visitor is on a studio's own custom domain — serve
            // ONLY the public site tree. `/` resolves via the
            // hostname; deep paths still route through PublicSite.
            <Routes>
              <Route path="/s/:siteSlug/*" element={<PublicSiteV2 />} />
              <Route path="/site-preview" element={<SitePreviewFrame />} />
              <Route path="/*" element={<PublicSiteV2 host />} />
            </Routes>
          ) : (
          <Routes>
            {/* Public auth route */}
            <Route path="/auth" element={<Auth />} />
            {/* P0-FIX-1 — Password-recovery landing.  Supabase's
                reset-password email links here with a session token
                baked into the URL hash. */}
            <Route path="/auth/reset" element={<AuthReset />} />
            {/* LAUNCH-EA-1 — public waitlist capture. Where marketing's
                "Sign up" CTAs point during the early-access window
                (IS_EARLY_ACCESS in config/launch.ts) instead of /auth's
                account-creation form directly. */}
            {/* LAUNCH-2 — after launch this page is a dead end: its
                copy says "our public launch on September 14", which
                has passed, and it posts to waitlist-signup for a
                product that is now simply on sale. Old invite emails,
                bookmarks and search results still land here, so it
                redirects into signup rather than 404ing or collecting
                a waitlist entry nobody will action. The component is
                untouched and comes straight back if IS_EARLY_ACCESS
                is ever true again. */}
            <Route
              path="/early-access"
              element={
                IS_EARLY_ACCESS ? (
                  <EarlyAccessSignup />
                ) : (
                  <Navigate to="/auth?mode=signup" replace />
                )
              }
            />
            {/* Public winter waitlist, independent of account signup availability. */}
            <Route path="/waitlist" element={<EarlyAccessSignup />} />
            
            {/* Legal pages */}
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<Cookies />} />
            {/* Public marketing / info page — About, Support, Learn. Content
                will be filled in later; this scaffold locks the route. */}
            {/* LAUNCH-5 — signed in, /orbit opens the studio (see
                MarketingOrStudio); `/orbit?preview` still shows the page. */}
            <Route path="/orbit" element={<MarketingOrStudio />} />
            <Route path="/about" element={<Marketing />} />
            {/* NOVA-ENV-1 — "Nova and the Environment". Public, no sign-in;
                the green button under Nova's cards on /orbit leads here.
                A static path, so it can never fall through to the
                `/:orgSlug` intake catch-all (and it is not the /nova
                design showcase below, which is a different page). */}
            <Route path="/nova/environment" element={<NovaEnvironment />} />
            <Route path="/pixieset" element={<PixiesetMigration />} />
            <Route path="/migrate-from-pixieset" element={<PixiesetMigration />} />
            <Route path="/video/:token" element={<CustomDomainProvider><PublicShell><PublicVideoProof /></PublicShell></CustomDomainProvider>} />

            {/* Design system showcases (dev / review only) */}
            <Route path="/nova" element={<NovaShowcase />} />
            {/* #437 — 2026 kit demo, linked from Settings → Developer. */}
            <Route path="/nova-kit" element={<NovaKitShowcase />} />
            {/* DEMO-2 — /aperture-demo (#494 rev) and /playground
                (#494 Pass 6) are UNROUTED, deliberately.
                WHY: both render literal "Placeholder" content —
                ApertureDemo's DestinationPanel prints "Placeholder /
                Card 1..3", and /playground is a link hub into it.
                DevOnlyRoute only 404'd them for non-privileged
                visitors, which is the wrong threat model for a
                keynote: the person most likely to fumble a URL on
                stage is the signed-in admin, and for them the gate
                passes straight through to placeholder copy.
                The page files stay on disk (src/pages/ApertureDemo.tsx,
                src/pages/DemoPlayground.tsx) — restore the lazy
                imports above, the DevOnlyRoute import, and these two
                <Route> lines to bring them back after the demo.
                NOTE: the shipped chrome lives in
                src/components/shared/AppApertureBar.tsx and is
                unaffected. */}
            <Route
              path="/onboarding"
              element={
                <OnboardingWizardPreview
                  onComplete={(landingPath) => {
                    // Persist the "I'm done with onboarding" flag
                    // BEFORE redirecting — otherwise ProtectedRoute
                    // sees onboarding_complete !== 'true' on / and
                    // bounces the user right back into the wizard.
                    // That was the infinite-loop bug new signups hit.
                    syncStorage.set('onboarding_complete', 'true');
                    // ONB-5 — the wizard now says where it wants to
                    // land: Passport, looking at the planet it just
                    // granted. It also absorbed the "Meet Nova" step,
                    // so /nova-intro is no longer the hand-off — it
                    // stays routed for anyone replaying it, and for
                    // the legacy flow, which still asks for it.
                    //
                    // ONB-4 (legacy): Welcome → Wizard → NovaIntro → Today.
                    window.location.href =
                      landingPath ?? (ONB5_FLOW ? PASSPORT_PATH : '/nova-intro');
                  }}
                />
              }
            />
            {/* ONB-4 — /welcome restored to the 10-slide editorial
                brand-story sequence. Fresh signups land here first;
                on complete WelcomeSequence hands off to /onboarding. */}
            <Route path="/welcome" element={<WelcomeSequence />} />
            {ONB5_DEV_SANDBOX && (
              <Route path="/admin/preview/setup" element={
                <AdminRoute>
                  <OnboardingSandbox />
                </AdminRoute>
              } />
            )}
            <Route path="/nova-intro" element={<NovaIntro />} />
            <Route path="/tour" element={<FeatureTour />} />
            <Route path="/tour/orbit" element={<OrbitTourSequence redirectTo="/" />} />

            {/* DEMO-2 — the /m/* mobile shell is UNROUTED, deliberately.
                WHY: it is an abandoned prototype superseded by the
                native iOS app, and it was the single worst demo hazard
                on the web app:
                  • It sat OUTSIDE ProtectedRoute and outside
                    DevOnlyRoute — /m was a fully public URL. Signed
                    out, useMobileToday()/useMobileClients() fall back
                    to fixtures, so any visitor saw "Good morning,
                    Rennan", "Martinez Wedding", "Cypress Studios" as
                    if it were live studio data.
                  • CaptureSheet's Save button persists nothing but
                    fires a "Captured — saved to your inbox" success
                    toast, so the surface actively lies about writing.
                  • Nothing outside the /m tree ever linked to it
                    (verified: only TabBar / mobile pages / useTabAccent
                    reference /m paths), and capacitor.config.ts points
                    at the site root, not /m — so no shipped surface
                    depends on these routes.
                Files kept on disk: src/components/mobile/* (MobileShell,
                TabBar, CaptureSheet) and src/pages/mobile/*. Restore
                the lazy imports above and this block to revive it —
                but wire CaptureSheet's Save to Supabase first.
                Bare `/m` is pinned to NotFound so it renders a clean
                404 instead of falling through to the `/:orgSlug`
                catchall's "Studio Not Found". React Router v6 ranks
                static segments above dynamic ones, so position here
                doesn't matter. */}
            <Route path="/m" element={<NotFound />} />

            {/* Public routes (no auth required) — Wave 6 wraps every
                client-facing surface in CustomDomainProvider so
                verified custom-domain visitors get the studio's
                brand colors / font / logo applied. */}
            <Route path="/portal/:token" element={<CustomDomainProvider><PublicShell><PublicClientPortal /></PublicShell></CustomDomainProvider>} />
            {/* GAL-7 — QR landing: access code + "view in browser".
                Must contain "/portal/<token>" so the iOS in-app
                scanner keeps resolving the token from this URL. */}
            <Route path="/portal/:token/app" element={<CustomDomainProvider><PortalAppLanding /></CustomDomainProvider>} />
            <Route path="/inquiry-portal/:inquiryId" element={<PublicShell><InquiryPortal /></PublicShell>} />
            <Route path="/embed/intake/:orgSlug" element={<PublicIntakeForm />} />
            <Route path="/embed/intake/:orgSlug/:formSlug" element={<PublicIntakeForm />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            {/* FOUNDING-1 — a Founding Member's kit: pick a hoodie, a pin
                and a patch from a private per-person link (no sign-in).
                `/founding/demo` is a sample member. Bare `/founding` is
                pinned here so it doesn't fall through to the `/:orgSlug`
                intake-form catch-all as a "Studio Not Found". */}
            <Route path="/founding/:token" element={<FoundingKit />} />
            <Route path="/founding" element={<FoundingKit />} />
            {/* SHOP-1 — the Launch Edition shop. Public, no sign-in; checkout
                is Stripe's. Pinned like /founding so it never falls through
                to the `/:orgSlug` intake catch-all. */}
            <Route path="/shop" element={<Shop />} />
            {/* GIFT-1 — Rennan's ACT II · ORBIT thank-you: a private
                per-person link to pick hoodies, pins and patches (no
                sign-in, no payment). `/thanks/demo` is a sample person.
                Bare `/thanks` is pinned like /founding so it doesn't
                fall through to the `/:orgSlug` intake catch-all. */}
            <Route path="/thanks/:token" element={<Gift />} />
            <Route path="/thanks" element={<Gift />} />
            {/* GAL-SPLASH — every route below wrapped in
                <CustomDomainProvider> is a surface a CLIENT sees, not a
                studio. Adding one here means adding its path prefix to
                `skipPrefixes` in components/shared/SplashScreen.tsx,
                or the client opens on Orbit's brand splash instead of
                the studio's work. That list had already drifted:
                /gallery/ and /video/ were missing. */}
            <Route path="/delivery/:slug" element={<CustomDomainProvider><PublicDelivery /></CustomDomainProvider>} />
            {/* GAL-2 — standalone public gallery portal.
                • `/gallery/demo` MUST come first so the literal
                  "demo" path matches the demo handler instead of
                  being interpreted as a gallery UUID.
                • `/gallery/:galleryId` fetches via the SECURITY
                  DEFINER RPC introduced in the same wave. */}
            <Route path="/gallery/demo" element={<CustomDomainProvider><DemoGalleryPortal /></CustomDomainProvider>} />
            <Route path="/gallery/:galleryId" element={<CustomDomainProvider><PublicGallery /></CustomDomainProvider>} />
            {/* MKTG-GAL14c — live demos of one design / one template. */}
            <Route path="/demo/gallery/:design" element={<LiveDemoGallery />} />
            <Route path="/demo/site/:template" element={<LiveDemoSite />} />
            {/* SITES-V2 — public creator website (real paths under the
                slug: /work/:project, /journal/:post, /inquire …) and the
                Site editor's preview frame (renders only what the
                editor posts to it). */}
            <Route path="/s/:siteSlug/*" element={<PublicSiteV2 />} />
            <Route path="/site-preview" element={<SitePreviewFrame />} />

            <Route path="/sign/:token" element={<CustomDomainProvider><PublicShell><SignContract /></PublicShell></CustomDomainProvider>} />
            {/* New Document Builder public sign page — proposals/
                contracts/invoices unified into one signable doc. */}
            <Route path="/d/:token" element={<CustomDomainProvider><SignDocument /></CustomDomainProvider>} />
            {/* Public booking page — leads pick a slot from any
                studio's booking link. Calendly-style, but on Orbit. */}
            <Route path="/book/:slug" element={<CustomDomainProvider><PublicShell><PublicBooking /></PublicShell></CustomDomainProvider>} />
            
            {/* Admin routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            }>
              <Route index element={<AdminDashboard />} />
              <Route path="accounts" element={<AdminAccounts />} />
              <Route path="accounts/:accountId" element={<AdminAccountDetail />} />
              {/* MC-1 — Nova cofounder mode. */}
              <Route path="nova" element={<AdminNova />} />
              <Route path="email" element={<AdminEmailCenter />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="audit" element={<AdminAuditLog />} />
              <Route path="revenue" element={<AdminRevenue />} />
              <Route path="beta-readiness" element={<AdminBetaReadiness />} />
              <Route path="beta-testers" element={<AdminBetaTesters />} />
              {/* LAUNCH-EA-1 — waitlist review + invite. */}
              <Route path="early-access" element={<AdminEarlyAccess />} />
              <Route path="founding" element={<AdminFounding />} />
              <Route path="shop" element={<AdminShop />} />
              <Route path="gifts" element={<AdminGifts />} />
              <Route path="beta-nda" element={<AdminBetaNda />} />
              <Route path="diagnostics" element={<AdminDiagnostics />} />
              <Route path="nova-studio" element={<AdminNovaStudio />} />
              <Route path="demo-access" element={<AdminDemoAccess />} />
              {/* MEDIA-1d — drag-and-drop demo photo uploader; exists
                  because no service-role key is reachable on a
                  Lovable-managed project. */}
              <Route path="demo-media" element={<AdminDemoMedia />} />
              {/* DEVMODE-1 — developer mode: the first-run flows from
                  a signed-in admin account. */}
              <Route path="preview" element={<AdminPreview />} />
            </Route>
            
            {/* Protected routes */}
            <Route element={
              <ProtectedRoute>
                <RootLayout />
              </ProtectedRoute>
            }>
              <Route path="/" element={
                <ErrorBoundary>
                  <Index />
                </ErrorBoundary>
              } />
              <Route path="/projects" element={
                <ErrorBoundary>
                  <Projects />
                </ErrorBoundary>
              } />
              <Route path="/clients" element={
                <ErrorBoundary>
                  <Clients />
                </ErrorBoundary>
              } />
              {/* INVOICE-1 — the money page. There was no view across
                  invoices at all: /billing/:projectId answers "what
                  did this project invoice", and nothing answered "who
                  hasn't paid me". */}
              <Route path="/invoices" element={
                <ErrorBoundary>
                  <GoogieInvoices />
                </ErrorBoundary>
              } />
              {/* INVOICE-2 — the invoice editor. Top-level and not
                  under /studio/:projectId, because an invoice does not
                  need a project: "renew the retainer" is billed to a
                  client, not to a job. `new` is handled by the same
                  component, which creates the row on first save. */}
              <Route path="/invoices/:documentId" element={
                <ErrorBoundary>
                  <GoogieInvoiceEditor />
                </ErrorBoundary>
              } />
              {/* PLANNER-1 — the Planner artboard is the calendar.
                  It replaced three implementations that lived behind
                  two flags in CalendarPage: `useGoogie()` had returned
                  true unconditionally since GOOGIE-15, so the route
                  always landed on GoogieCalendar and the NewCalendar
                  branch under it — where CAL-PRO-1 through -3 were
                  built — was never once reachable. All three are
                  deleted as of PLANNER-4. */}
              <Route path="/calendar" element={
                <ErrorBoundary>
                  <PlannerCalendar />
                </ErrorBoundary>
              } />
              <Route path="/settings" element={
                <ErrorBoundary>
                  <Settings />
                </ErrorBoundary>
              } />
              <Route path="/settings/templates" element={
                <ErrorBoundary>
                  <SettingsTemplates />
                </ErrorBoundary>
              } />
              <Route path="/client/:projectId" element={
                <ErrorBoundary>
                  <ClientPortal />
                </ErrorBoundary>
              } />
              <Route path="/studio/:projectId" element={
                <ErrorBoundary>
                  <ProjectWorkspace />
                </ErrorBoundary>
              } />
              {/* GOOGIE-14 — nested under the project so the breadcrumb
                  and the back path are both real, and so a contract can
                  never be opened without the project that owns it. */}
              <Route path="/studio/:projectId/contract/:documentId" element={
                <ErrorBoundary>
                  <ContractStudioRoute />
                </ErrorBoundary>
              } />
              <Route path="/intake/new" element={
                <ErrorBoundary>
                  <IntakeForm />
                </ErrorBoundary>
              } />
              <Route path="/inquiries" element={
                <ErrorBoundary>
                  <Inquiries />
                </ErrorBoundary>
              } />
              <Route path="/billing/:projectId" element={
                <ErrorBoundary>
                  <BillingPage />
                </ErrorBoundary>
              } />
              <Route path="/assistant" element={
                <ErrorBoundary>
                  <AssistantRoute />
                </ErrorBoundary>
              } />
              <Route path="/deliveries" element={
                <ErrorBoundary>
                  <Deliveries />
                </ErrorBoundary>
              } />
              <Route path="/galleries" element={
                <ErrorBoundary>
                  <Galleries />
                </ErrorBoundary>
              } />
              {/* GAL-5 — full-screen gallery editor. Own chrome
                  (top bar + rail) rendered inside RootLayout's
                  outlet, same pattern as DeliveryDetail. */}
              <Route path="/galleries/:galleryId" element={
                <ErrorBoundary>
                  <GalleryEditor />
                </ErrorBoundary>
              } />
              {/* Galaxy — the studio's planet collection (#327). Always
                  routed; the sidebar entry is hidden when gamification
                  is off, but the URL still works for direct visits. */}
              <Route path="/galaxy" element={
                <ErrorBoundary>
                  <Galaxy />
                </ErrorBoundary>
              } />
              <Route path="/deliveries/:id" element={
                <ErrorBoundary>
                  <DeliveryDetail />
                </ErrorBoundary>
              } />
              {/* Workflows / automations routes killed (#341) per Aug 29
                  launch cut list. Files kept in the codebase for potential
                  future re-enable, but no user-facing route surfaces the
                  builder. If revisited post-launch, restore these routes
                  and the sidebar entry in AppSidebar.tsx. */}
              <Route path="/sites" element={
                <ErrorBoundary>
                  <SitesAdmin />
                </ErrorBoundary>
              } />
              {/* Wave EC-2 — Email Center (studio-facing). Sent
                  history + per-template overrides. See
                  src/pages/EmailCenter.tsx. */}
              <Route path="/emails" element={
                <ErrorBoundary>
                  <EmailCenter />
                </ErrorBoundary>
              } />
              {/* NCC-2 — Learn tab. Educational surface with 15 info
                  cards across three sections (Getting Started / Every
                  Tab / Why Orbit). Each card opens an Apple-Home-style
                  detail modal with WHY IT MATTERS + HOW TO... copy. */}
              <Route path="/learn" element={
                <ErrorBoundary>
                  <Learn />
                </ErrorBoundary>
              } />
              <Route path="/expenses" element={
                <ErrorBoundary>
                  <Expenses />
                </ErrorBoundary>
              } />
              {/* OAuth callbacks — Google Calendar, QuickBooks, Xero
                  all hand back ?code=&state= here. Single page that
                  detects which provider and routes to the right
                  edge function. Auth-required so we have a JWT to
                  forward. */}
              <Route path="/oauth/:provider" element={
                <ErrorBoundary>
                  <OAuthCallback />
                </ErrorBoundary>
              } />
              {/* GOOGIE-2 — Forms gets a real route. The redesign gives
                  it a whole screen, and mounting a screen on a Settings
                  row is how a page ends up unlinkable. This route is
                  nested inside RootLayout so it lands under the sky;
                  with the redesign off it redirects into Settings
                  exactly as `/forms` always has (see FormsRoute). */}
              <Route path="/forms" element={
                <ErrorBoundary>
                  <FormsRoute />
                </ErrorBoundary>
              } />
            </Route>

            {/* Nova Design is now a MODE of the Nova page — the old
                standalone route redirects so bookmarks + deep links
                keep working.  The surface itself (pages/NovaDesign.tsx)
                is embedded by Assistant.tsx behind ?mode=design. */}
            <Route path="/nova-design" element={<Navigate to={NOVA_DESIGN_SHIPPED ? "/assistant?mode=design" : "/assistant"} replace />} />
            
            {/* Internal-route redirects MUST come before the
                /:orgSlug catchall — otherwise the public-intake-form
                route swallows requests for /forms (showing a
                "Studio Not Found" error) when the studio actually
                meant to manage their intake forms. We redirect to
                the right internal location instead. */}
            <Route path="/forms" element={<Navigate to="/settings?tab=intake-forms" replace />} />
            <Route path="/templates" element={<Navigate to="/settings?tab=intake-forms" replace />} />

            {/* Root-level slug route for custom domain (orbtcrm.com/:slug) - must be last before catch-all */}
            <Route path="/:orgSlug" element={<PublicIntakeForm />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
          )}
          {/* Global Nova launcher (⌘K). Self-hides on public /
              auth / mobile / admin surfaces. Showcase tour lives here
              too — same reasoning: needs router context, and has to
              survive route changes instead of unmounting with them. */}
          {!isCustomHostname() && (
            <>
              <NovaLauncher />
              <ShowcaseTour />
            </>
          )}
        </Suspense>
      </BrowserRouter>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    {/* Wave 13a — Real light/dark mode restored.  next-themes stays
        in the tree for any `useTheme()` consumers (Sidebar, etc.),
        but the source of truth is now our own `useThemeMode` hook
        which drives `<html data-theme>` + the `.dark` / `.new-shell`
        classes.  We point next-themes at the same localStorage key
        (`orbit_theme_mode`) so both systems agree on the current
        mode.  `forcedTheme` is gone; `enableSystem={false}` keeps
        the mode explicit (Light or Dark, never "System"). */}
    <ThemeProvider
      attribute="class"
      defaultTheme="dark"
      enableSystem={false}
      disableTransitionOnChange
      storageKey="orbit_theme_mode"
    >
      <TooltipProvider>
        <AuthProvider>
          <DemoModeProvider>
            <ShowcaseModeProvider>
            <DemoStoreProvider>
            <CinemaModeProvider>
              <UpgradeModalProvider>
                <NovaEmotionProvider>
                  <ErrorBoundary name="app-root">
                    {/* SCALE-2 — keeps the error tracker's user/org
                        tags in sync with the session. Renders nothing;
                        sends opaque uuids only, never an email. */}
                    <ObservabilityIdentity />
                    <AppContent />
                    <UpgradeModal />
                  </ErrorBoundary>
                </NovaEmotionProvider>
              </UpgradeModalProvider>
            </CinemaModeProvider>
            </DemoStoreProvider>
            </ShowcaseModeProvider>
          </DemoModeProvider>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

/**
 * LAUNCH-5 — `/orbit` is the marketing page, but it is linked from the
 * invite and waitlist emails, the 404 page, the shop and the sign-in
 * back button, so a signed-in studio lands on it too — and was stuck
 * there behind "Join the waitlist", with the logo looping back to the
 * same page. Signed in, it now opens the studio home (`/`, Today).
 *
 * `?preview` keeps the marketing page for a signed-in look (how the
 * page gets reviewed). Visitors never wait on auth: the page renders
 * at once and the redirect happens only once a session is known.
 */
function MarketingOrStudio() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  if (user && !params.has("preview")) return <Navigate to="/" replace />;
  return <Marketing />;
}

/** GOOGIE-1 — one of the two Nova surfaces, by preference. */
function FormsRoute() {
  const [googie] = useGoogie();
  // Off, `/forms` behaves as it always has — the intake-forms editor
  // lives in Settings and there is no second one.
  if (!googie) return <Navigate to="/settings?tab=intake-forms" replace />;
  return <GoogieForms />;
}

function AssistantRoute() {
  const [googie] = useGoogie();
  // NOVA-OFF-2 — "her tab goes away" has to mean the URL too. iOS
  // drops .chat from RootTabView, so there is no way back in; on the
  // web the route stayed live and a bookmark walked straight into a
  // working chat. Land on the switch that turned it off. (This also
  // closes the mic: nova-stt and nova-tts are only called from
  // Assistant, which no longer mounts.)
  const [novaOn] = useNovaIntelligence();
  if (!novaOn) return <Navigate to="/settings?tab=nova" replace />;
  return googie ? <GoogieNova /> : <Assistant />;
}

export default App;
